/* eslint-disable react/no-unknown-property */
import React, { useState, useEffect, useRef, Suspense, useCallback, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Float, MeshDistortMaterial, shaderMaterial } from '@react-three/drei';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import Lenis from '@studio-freight/lenis';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import * as THREE from 'three';
import { extend } from '@react-three/fiber';
import {
  Sun, Moon, Menu, X, ArrowRight, CheckCircle2,
  Search, Layers, Target, Layout, Palette, RefreshCw, Database,
  ChevronRight, Mail, MapPin, Users, Sparkles, Calendar, Clock,
  FileSearch, Zap, Shield, CreditCard
} from 'lucide-react';
import './App.css';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const CALENDAR_URL = 'https://calendar.app.google/U5kPQxCXYBTN9FNd7';

// Custom Shader Material - Octahedron Raymarching with Brand Green
const OctahedronShaderMaterial = shaderMaterial(
  {
    uTime: 0,
    uResolution: new THREE.Vector2(1, 1),
    uDarkMode: 1.0,
  },
  // Vertex Shader
  `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment Shader - Octahedron raymarching with brand green palette
  `
    uniform float uTime;
    uniform vec2 uResolution;
    uniform float uDarkMode;
    varying vec2 vUv;

    #define PI 3.14159265359

    // Rotation matrix
    mat2 rot(float a) {
      float s = sin(a);
      float c = cos(a);
      return mat2(c, -s, s, c);
    }

    // Brand green palette - subtle and aesthetic
    vec3 palette(float t, float darkMode) {
      // AUMRAA brand colors: green (#2F9E44), lime-yellow (#CFCF2A)
      vec3 a = vec3(0.15, 0.35, 0.15);  // Base - dark green tint
      vec3 b = vec3(0.15, 0.25, 0.1);   // Amplitude - subtle
      vec3 c = vec3(0.8, 1.0, 0.5);     // Frequency
      vec3 d = vec3(0.25, 0.4, 0.15);   // Phase - green-shifted
      
      vec3 col = a + b * cos(6.28318 * (c * t + d));
      
      // Make it more muted and elegant
      col = mix(col * 0.6, col * 0.9, darkMode);
      return col;
    }

    // Signed Distance Function: Octahedron
    float sdOctahedron(vec3 p, float s) {
      p = abs(p);
      return (p.x + p.y + p.z - s) * 0.57735027;
    }

    // Map function
    float map(vec3 p, float time) {
      // Slow twist for elegance
      p.xy *= rot(p.z * 0.08 + time * 0.015);

      // Repetition
      float repSize = 3.2;
      vec3 q = mod(p, repSize) - repSize * 0.5;

      // Gentle rotation
      q.xy *= rot(time * 0.12);
      q.xz *= rot(time * 0.06);

      // Shape with shell
      float shapeSize = 0.5;
      float d = sdOctahedron(q, shapeSize);
      float thickness = 0.08;
      d = abs(d) - thickness;

      return d;
    }

    void main() {
      vec2 uv = (vUv * 2.0 - 1.0);
      uv.x *= uResolution.x / uResolution.y;
      vec2 uv0 = uv;

      float time = uTime * 0.3;

      // Camera moving forward slowly
      vec3 ro = vec3(0.0, 0.0, -3.0 + time * 0.8);
      vec3 rd = normalize(vec3(uv, 1.0));
      vec3 col = vec3(0.0);

      float t = 0.0;

      // Raymarching loop - reduced iterations for subtlety
      for (int i = 0; i < 32; i++) {
        vec3 p = ro + rd * t;
        float d = map(p, time);
        t += d;

        if (d < 0.01 || t > 25.0) break;

        // Subtle glow
        float glowBase = 0.0012;
        float glow = glowBase / (abs(d) + 0.001);
        float fade = exp(-t * 0.12);

        vec3 shapeColor = palette(length(uv0) * 0.5 + time * 0.15 + float(i) * 0.02, uDarkMode);
        col += shapeColor * glow * fade * 0.7;
      }

      // Background based on dark/light mode
      vec3 darkBg = vec3(0.0, 0.02, 0.01);
      vec3 lightBg = vec3(0.96, 0.98, 0.95);
      vec3 bg = mix(lightBg, darkBg, uDarkMode);

      // Subtle vignette
      float vignette = 1.0 - length(uv0) * 0.25;
      vignette = clamp(vignette, 0.0, 1.0);

      // Tone mapping - adjusted for both modes
      float exposure = mix(1.2, 0.6, uDarkMode);
      col = 1.0 - exp(-col * exposure);

      // Gamma correction
      col = pow(col, vec3(0.4545));

      // For light mode, invert and enhance the effect to show darker shapes
      if (uDarkMode < 0.5) {
        // In light mode, create darker green shapes on light background
        vec3 shapeColor = vec3(0.18, 0.42, 0.22); // Darker brand green
        float shapeIntensity = length(col) * 1.5;
        shapeIntensity = clamp(shapeIntensity, 0.0, 1.0);
        col = mix(lightBg, shapeColor, shapeIntensity * vignette * 0.3);
      } else {
        // Dark mode - glowing shapes
        float intensity = 0.4;
        col = bg + col * intensity * vignette;
      }

      // Very subtle grain for texture
      float grain = (fract(sin(dot(vUv * uTime * 0.1, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) * 0.015;
      col += grain;

      gl_FragColor = vec4(col, 1.0);
    }
  `
);

extend({ OctahedronShaderMaterial });

// Shader Background Component - Octahedron Raymarching
function ShaderBackground({ isDark }) {
  const meshRef = useRef();
  const { viewport, size } = useThree();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.material.uTime = state.clock.elapsedTime;
      meshRef.current.material.uDarkMode = isDark ? 1.0 : 0.0;
    }
  });

  return (
    <mesh ref={meshRef} scale={[viewport.width, viewport.height, 1]}>
      <planeGeometry args={[1, 1]} />
      <octahedronShaderMaterial
        uResolution={[size.width, size.height]}
        uDarkMode={isDark ? 1.0 : 0.0}
      />
    </mesh>
  );
}

// 3D Floating Sphere Component
function FloatingSphere({ position, color, speed = 1, distort = 0.3, scale = 1 }) {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = state.clock.elapsedTime * 0.1 * speed;
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.15 * speed;
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.8}>
      <mesh ref={meshRef} position={position} scale={scale}>
        <sphereGeometry args={[1, 64, 64]} />
        <MeshDistortMaterial
          color={color}
          transparent
          opacity={0.4}
          distort={distort}
          speed={1.5}
          roughness={0.3}
        />
      </mesh>
    </Float>
  );
}

// 3D Scene Component
function Scene({ isDark }) {
  return (
    <>
      <ShaderBackground isDark={isDark} />
      <ambientLight intensity={0.3} />
      <pointLight position={[10, 10, 10]} intensity={0.5} />
    </>
  );
}

// Lenis Instance (global)
let lenisInstance = null;

// Booking Modal Component
function BookingModal({ isOpen, onClose }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-aumraa-black/60 dark:bg-black/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="relative w-full max-w-5xl h-[85vh] bg-white dark:bg-aumraa-black rounded-3xl overflow-hidden shadow-2xl border border-white/20 animate-in fade-in zoom-in-95 duration-300">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
        >
          <X size={24} className="text-aumraa-black dark:text-white" />
        </button>
        <iframe
          src={CALENDAR_URL}
          width="100%"
          height="100%"
          frameBorder="0"
          title="Schedule a Call"
          className="w-full h-full"
        />
      </div>
    </div>
  );
}

// Smooth scroll to section helper
const scrollToSection = (id, offset = 0) => {
  const element = document.getElementById(id);
  if (element) {
    const y = element.getBoundingClientRect().top + window.scrollY + offset;
    if (lenisInstance) {
      lenisInstance.scrollTo(y, { duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    } else {
      gsap.to(window, { scrollTo: { y: y, autoKill: false }, duration: 1.2, ease: 'power3.inOut' });
    }
  }
};

// Navigation Component
function Navigation({ isDark, toggleTheme, activeSection }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'process', label: 'Process' },
    { id: 'about', label: 'About' },
    { id: 'subscription', label: 'Subscription' },
    { id: 'contact', label: 'Contact' }
  ];

  const handleNavClick = (id) => {
    scrollToSection(id, -80);
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (lenisInstance) {
      lenisInstance.scrollTo(0, { duration: 1.2 });
    } else {
      gsap.to(window, { scrollTo: { y: 0 }, duration: 1.2, ease: 'power3.inOut' });
    }
  };

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
          ? 'bg-white/95 dark:bg-aumraa-black/95 backdrop-blur-lg shadow-lg'
          : 'bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="logo-home-btn"
            onClick={handleLogoClick}
            className="flex items-center gap-3 group"
          >
            <img
              src="/assets/logo-symbol.svg"
              alt="AUMRAA"
              className="h-10 w-auto hero-logo"
            />
            <span className={`font-bold text-xl tracking-tight transition-colors duration-300 ${isScrolled
              ? 'text-aumraa-black dark:text-white'
              : isDark ? 'text-white' : 'text-aumraa-black'
              }`}>
              AUMRAA
            </span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => handleNavClick(link.id)}
                className={`nav-link text-sm uppercase tracking-wider transition-colors duration-300 ${activeSection === link.id ? 'text-aumraa-green' : ''
                  } ${isScrolled
                    ? 'text-aumraa-black/70 hover:text-aumraa-green dark:text-white/80 dark:hover:text-aumraa-green'
                    : isDark
                      ? 'text-white/80 hover:text-white'
                      : 'text-aumraa-black/70 hover:text-aumraa-black'
                  }`}
              >
                {link.label}
              </button>
            ))}

            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-all duration-300 ${isScrolled
                ? 'bg-gray-100 hover:bg-gray-200 dark:bg-white/10 dark:hover:bg-white/20'
                : isDark
                  ? 'bg-white/10 hover:bg-white/20'
                  : 'bg-aumraa-black/10 hover:bg-aumraa-black/20'
                }`}
            >
              {isDark ? <Sun size={20} className="text-aumraa-yellow" /> : <Moon size={20} />}
            </button>
          </div>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2"
          >
            {mobileMenuOpen ? (
              <X size={24} className={isDark ? 'text-white' : 'text-aumraa-black'} />
            ) : (
              <Menu size={24} className={isScrolled ? (isDark ? 'text-white' : 'text-aumraa-black') : (isDark ? 'text-white' : 'text-aumraa-black')} />
            )}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu"
          >
            <div className="flex flex-col items-center gap-8">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.id}
                  data-testid={`mobile-nav-${link.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleNavClick(link.id)}
                  className="text-2xl text-white font-medium tracking-wider"
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={toggleTheme}
                className="mt-4 p-3 bg-white/10 rounded-full"
              >
                {isDark ? <Sun size={24} className="text-aumraa-yellow" /> : <Moon size={24} className="text-white" />}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Hero Section
function HeroSection({ isDark, onOpenBooking }) {
  const heroRef = useRef();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const titles = heroRef.current.querySelectorAll('.hero-title');
      const subtitles = heroRef.current.querySelectorAll('.hero-subtitle');
      const ctas = heroRef.current.querySelectorAll('.hero-cta');

      if (titles.length > 0) {
        gsap.fromTo(titles,
          { y: 100, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.2, ease: 'power4.out', stagger: 0.2 }
        );
      }
      if (subtitles.length > 0) {
        gsap.fromTo(subtitles,
          { y: 50, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, delay: 0.6, ease: 'power3.out' }
        );
      }
      if (ctas.length > 0) {
        gsap.fromTo(ctas,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, delay: 1, ease: 'power3.out' }
        );
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      className={`hero-section relative min-h-screen flex items-center justify-center transition-colors duration-500`}
    >
      {/* 3D Background removed from here for parallax */}
      <div className="absolute inset-0 z-0">
        {/* Placeholder for spacing if needed, but Canvas is now fixed global */}
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <div className="overflow-hidden mb-4">
          {/* Subtitle removed as per request */}
        </div>

        <div className="overflow-hidden">
          <h1 className={`hero-title text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-2 transition-colors duration-500 ${isDark ? 'text-white' : 'text-aumraa-black'
            }`}>
            ROOTED IN
          </h1>
        </div>
        <div className="overflow-hidden">
          <h1 className="hero-title text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-2">
            <span className="gradient-text">EMPOWERED INGENUITY</span>
          </h1>
        </div>
        <div className="overflow-hidden mb-8">
          <h1 className={`hero-title text-4xl md:text-6xl lg:text-7xl font-bold leading-tight transition-colors duration-500 ${isDark ? 'text-white' : 'text-aumraa-black'
            }`}>
            WE DESIGN REPEATABLE PRODUCT SYSTEMS
          </h1>
        </div>

        <div className="overflow-hidden">
          <p className={`hero-subtitle text-lg md:text-xl max-w-2xl mx-auto mb-10 transition-colors duration-500 ${isDark ? 'text-white/60' : 'text-aumraa-grey'
            }`}>
            We deliver systems-driven product design through a plug-and-play design engine — helping teams move fast, stay focused, and build without design chaos.
          </p>

          {/* Feature List */}
          <div className="hero-subtitle flex flex-col md:flex-row items-center justify-center gap-4 md:gap-12 mb-12 text-base md:text-lg font-medium text-aumraa-grey/80 dark:text-white/70">
            <div className="flex items-center gap-3 group cursor-default">
              <div className="p-2 rounded-full bg-aumraa-green/10 dark:bg-white/10 group-hover:bg-aumraa-green/20 group-hover:scale-110 transition-all duration-300">
                <Zap size={20} className="text-aumraa-green group-hover:rotate-12 transition-transform duration-300" />
              </div>
              <span>No hiring overhead</span>
            </div>

            <div className="flex items-center gap-3 group cursor-default">
              <div className="p-2 rounded-full bg-aumraa-green/10 dark:bg-white/10 group-hover:bg-aumraa-green/20 group-hover:scale-110 transition-all duration-300">
                <Shield size={20} className="text-aumraa-green group-hover:scale-110 transition-transform duration-300" />
              </div>
              <span>Low-risk design audit</span>
            </div>

            <div className="flex items-center gap-3 group cursor-default">
              <div className="p-2 rounded-full bg-aumraa-green/10 dark:bg-white/10 group-hover:bg-aumraa-green/20 group-hover:scale-110 transition-all duration-300">
                <CreditCard size={20} className="text-aumraa-green group-hover:rotate-12 transition-transform duration-500" />
              </div>
              <span>One flat monthly rate</span>
            </div>
          </div>
        </div>

        {/* Unified CTA */}
        <div className="hero-cta flex justify-center">
          <button
            onClick={onOpenBooking}
            data-testid="hero-cta-schedule"
            className="px-8 py-4 bg-aumraa-green/90 backdrop-blur-md border border-white/20 text-white font-semibold rounded-2xl hover:bg-aumraa-green hover:scale-105 transition-all shadow-lg shadow-aumraa-green/30 flex items-center justify-center gap-2"
          >
            <Calendar size={22} />
            Schedule a Call
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <div className={`w-6 h-10 border-2 rounded-full flex justify-center pt-2 transition-colors duration-500 ${isDark ? 'border-white/30' : 'border-aumraa-black/30'
          }`}>
          <div className="w-1.5 h-3 bg-aumraa-green rounded-full" />
        </div>
      </motion.div>
    </section>
  );
}

// Process Section (BANANA LEAF Framework)
function ProcessSection({ onOpenBooking }) {
  const sectionRef = useRef();

  const steps = [
    { letter: 'B', title: 'Business Alignment', desc: 'Understanding your goals, constraints, and success metrics' },
    { letter: 'A', title: 'Audience Insight', desc: 'Deep user research and market understanding' },
    { letter: 'N', title: 'Needs Analysis', desc: 'Identifying core problems and opportunities' },
    { letter: 'A', title: 'Architecture Design', desc: 'Structuring information and user flows' },
    { letter: 'N', title: 'Navigation & Interaction', desc: 'Creating intuitive pathways and behaviors' },
    { letter: 'A', title: 'Aesthetic Systems', desc: 'Visual design that scales and endures' },
    { letter: 'L', title: 'Launch Readiness', desc: 'Developer handoff with complete documentation' },
    { letter: 'E', title: 'Evolution Support', desc: 'Systems designed to grow with you' },
    { letter: 'A', title: 'Audit & Iterate', desc: 'Continuous improvement through feedback' },
    { letter: 'F', title: 'Framework Extension', desc: 'Expanding capabilities as needs evolve' }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      const processCards = sectionRef.current.querySelectorAll('.process-card');
      if (processCards.length > 0) {
        gsap.fromTo(processCards,
          { y: 60, opacity: 0 },
          {
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power3.out'
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="process"
      data-testid="process-section"
      className="section-padding leaf-pattern relative"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-aumraa-green font-medium tracking-widest text-sm uppercase">Our Framework</span>
          <h2 className="text-4xl md:text-5xl font-bold text-aumraa-black dark:text-white mt-4 mb-6">
            The <span className="gradient-text">BANANA LEAF</span> Framework
          </h2>
          <p className="text-lg text-aumraa-grey dark:text-white/60 max-w-2xl mx-auto">
            A systematic approach to design that adapts from solopreneurs to enterprise teams.
            Built for clarity, scalability, and momentum.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className="process-card p-6 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 hover:border-aumraa-green/50 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-aumraa-green to-aumraa-yellow flex items-center justify-center text-white font-bold text-xl mb-4 group-hover:scale-110 transition-transform">
                {step.letter}
              </div>
              <h3 className="font-semibold text-aumraa-black dark:text-white mb-2">{step.title}</h3>
              <p className="text-sm text-aumraa-grey dark:text-white/50">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* Design Engine Callout */}
        <div className="mt-16 p-8 md:p-12 rounded-3xl bg-gradient-to-br from-aumraa-black to-aumraa-black/90 dark:from-white/5 dark:to-white/10 border border-white/10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="text-aumraa-yellow" size={24} />
                <span className="text-aumraa-green font-medium tracking-wider text-sm uppercase">Proprietary System</span>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">The Design Engine</h3>
              <p className="text-white/60 mb-6">
                Our plug-and-play design system that powers every engagement. A modular, adaptable engine
                that brings structure to complexity and ensures every output is consistent, scalable, and
                developer-ready.
              </p>
              <button
                onClick={onOpenBooking}
                data-testid="design-engine-cta"
                className="flex items-center gap-2 text-aumraa-green font-medium hover:gap-3 transition-all"
              >
                Schedule a discovery call <ChevronRight size={18} />
              </button>
            </div>
            <div className="flex-shrink-0">
              <div className="w-40 h-40 rounded-2xl bg-gradient-to-br from-aumraa-green/20 to-aumraa-yellow/20 flex items-center justify-center">
                <img src="/assets/logo-symbol.svg" alt="Design Engine" className="w-24 h-24 opacity-80" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection() {
  const [services, setServices] = useState([]);
  const sectionRef = useRef();

  const iconMap = {
    'audit': Search,
    'research': Users,
    'analysis': Target,
    'architecture': Layers,
    'design': Palette,
    'revamp': RefreshCw,
    'system': Database
  };

  useEffect(() => {
    setServices([
      { id: 'design-audits', title: 'Design Audits', description: 'Comprehensive evaluation of your product\'s design ecosystem.', icon: 'audit' },
      { id: 'user-market-research', title: 'User & Market Research', description: 'Deep understanding of your users and market landscape.', icon: 'research' },
      { id: 'competitor-analysis', title: 'Competitor Analysis', description: 'Strategic assessment of competitive landscape.', icon: 'analysis' },
      { id: 'ux-architecture', title: 'UX Architecture', description: 'Structural foundations that make complexity navigable.', icon: 'architecture' },
      { id: 'ui-design', title: 'UI Design', description: 'Visual systems that communicate, guide, and delight.', icon: 'design' },
      { id: 'redesigns-revamps', title: 'Redesigns & Revamps', description: 'Thoughtful evolution of existing products.', icon: 'revamp' },
      { id: 'design-systems', title: 'Design Systems', description: 'Scalable systems with developer-ready handoff.', icon: 'system' }
    ]);
  }, []);

  useEffect(() => {
    if (services.length === 0) return;

    const ctx = gsap.context(() => {
      const serviceCards = sectionRef.current.querySelectorAll('.service-card');
      if (serviceCards.length > 0) {
        gsap.fromTo(serviceCards,
          { y: 80, opacity: 0 },
          {
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power3.out'
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [services]);

  return (
    <section
      ref={sectionRef}
      data-testid="services-section"
      className="section-padding relative"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-aumraa-green font-medium tracking-widest text-sm uppercase">What We Do</span>
          <h2 className="text-4xl md:text-5xl font-bold text-aumraa-black dark:text-white mt-4 mb-6">
            Product Design, <span className="gradient-text">Exclusively</span>
          </h2>
          <p className="text-lg text-aumraa-grey dark:text-white/60 max-w-2xl mx-auto">
            We focus on one thing and do it exceptionally well. No development, no marketing —
            pure design expertise that transforms products.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || Layout;
            return (
              <div
                key={service.id}
                data-testid={`service-${service.id}`}
                className="service-card p-8 rounded-2xl bg-white/60 dark:bg-white/5 backdrop-blur-md border border-white/40 dark:border-white/10 hover:border-aumraa-green/50 transition-all duration-300 shadow-xl"
              >
                <div className="service-icon mb-6">
                  <IconComponent size={24} />
                </div>
                <h3 className="text-xl font-semibold text-aumraa-black dark:text-white mb-3">{service.title}</h3>
                <p className="text-aumraa-grey dark:text-white/60">{service.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// About Section
function AboutSection() {
  const sectionRef = useRef();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const aboutContent = sectionRef.current.querySelector('.about-content');
      const aboutVisual = sectionRef.current.querySelector('.about-visual');

      if (aboutContent) {
        gsap.fromTo(aboutContent,
          { x: -60, opacity: 0 },
          {
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
            x: 0,
            opacity: 1,
            duration: 1,
            ease: 'power3.out'
          }
        );
      }
      if (aboutVisual) {
        gsap.fromTo(aboutVisual,
          { x: 60, opacity: 0 },
          {
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
            x: 0,
            opacity: 1,
            duration: 1,
            ease: 'power3.out'
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      data-testid="about-section"
      className="section-padding relative"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="about-content">
            <span className="text-aumraa-green font-medium tracking-widest text-sm uppercase">About AUMRAA</span>
            <h2 className="text-4xl md:text-5xl font-bold text-aumraa-black dark:text-white mt-4 mb-6">
              Clarity Creates <span className="gradient-text">Momentum</span>
            </h2>
            <div className="space-y-6 text-lg text-aumraa-grey dark:text-white/70">
              <p>
                AUMRAA is a design-first product studio built on the belief that good design
                creates effective products. We&apos;re a lean team of senior designers who bring
                systems thinking to every engagement.
              </p>
              <p>
                Our name draws from ancient wisdom — like rain that nourishes, sustains, and
                restores, we design systems that bring clarity to chaos and momentum to stagnation.
              </p>
              <p>
                We don&apos;t chase trends or speak in buzzwords. We build repeatable product systems
                that respect intelligence and reward effort.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold gradient-text">10+</div>
                <div className="text-sm text-aumraa-grey dark:text-white/50">Years Combined</div>
              </div>
              <div>
                <div className="text-3xl font-bold gradient-text">50+</div>
                <div className="text-sm text-aumraa-grey dark:text-white/50">Products Designed</div>
              </div>
              <div>
                <div className="text-3xl font-bold gradient-text">100%</div>
                <div className="text-sm text-aumraa-grey dark:text-white/50">Design Focus</div>
              </div>
            </div>
          </div>

          <div className="about-visual relative">
            <div className="aspect-square rounded-3xl bg-gradient-to-br from-aumraa-green/10 to-aumraa-yellow/10 dark:from-aumraa-green/20 dark:to-aumraa-yellow/20 p-12 flex items-center justify-center">
              <img
                src="/assets/logo-light-vertical.svg"
                alt="AUMRAA"
                className="w-2/3 max-w-xs hero-logo"
              />
            </div>
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-aumraa-yellow/20 blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 rounded-full bg-aumraa-green/20 blur-2xl" />
          </div>
        </div>

        {/* Values */}
        <div className="mt-20 grid md:grid-cols-4 gap-6">
          {[
            { title: 'Clarity', desc: 'As a competitive advantage' },
            { title: 'Consistency', desc: 'Builds trust over time' },
            { title: 'Systems', desc: 'Before screens, always' },
            { title: 'Craft', desc: 'Respects intelligence, rewards effort' }
          ].map((value, index) => (
            <div key={index} className="text-center p-6 rounded-3xl bg-white/60 dark:bg-white/5 backdrop-blur-md border border-white/40 dark:border-white/10 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-aumraa-green/20 to-aumraa-yellow/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-aumraa-green" size={24} />
              </div>
              <h4 className="font-semibold text-aumraa-black dark:text-white mb-2">{value.title}</h4>
              <p className="text-sm text-aumraa-grey dark:text-white/50">{value.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Subscription Section
function SubscriptionSection({ onOpenBooking }) {
  const sectionRef = useRef();
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const calculatePrice = (basePrice, period) => {
    if (period === 'monthly') return basePrice;
    if (period === 'quarterly') return Math.round(basePrice * 0.95); // 5% discount
    if (period === 'yearly') return Math.round(basePrice * 0.85); // 15% discount
    return basePrice;
  };

  const getPeriodLabel = (period) => {
    if (period === 'monthly') return '/mo';
    if (period === 'quarterly') return '/mo (billed quarterly)';
    if (period === 'yearly') return '/mo (billed yearly)';
    return '/mo';
  };

  const subscriptionTiers = [
    {
      id: 'basic',
      name: 'Basic',
      subtitle: 'Foundation & Audit',
      basePrice: 1500,
      description: 'For early-stage startups needing clear direction and consistent execution.',
      features: [
        'UX/UI Design implementation',
        'Basic Design System usage',
        'Weekly sync',
        'Slack validity'
      ],
      highlighted: false
    },
    {
      id: 'pro',
      name: 'Pro',
      subtitle: 'BANANA LEAF Framework',
      basePrice: 3500,
      description: 'Full product support with our proprietary framework to accelerate growth.',
      features: [
        'Full BANANA LEAF adoption',
        'Design System creation',
        'User Research & Testing',
        'Priority Support',
        '2 Senior Designers'
      ],
      highlighted: true
    },
    {
      id: 'premium',
      name: 'Premium',
      subtitle: 'Design Engine',
      basePrice: 6000,
      description: 'Enterprise-grade operations for maximum speed and scale.',
      features: [
        'Design Engine integration',
        'Multiple product streams',
        'Strategy & Leadership',
        'Unlimited requests',
        'Dedicated Design Team'
      ],
      highlighted: false
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = sectionRef.current.querySelectorAll('.pricing-card');
      gsap.fromTo(cards,
        { y: 50, opacity: 0 },
        {
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power3.out'
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="subscription"
      data-testid="subscription-section"
      className="section-padding relative"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-aumraa-green font-medium tracking-widest text-sm uppercase">Pricing</span>
          <h2 className="text-4xl md:text-5xl font-bold text-aumraa-black dark:text-white mt-4 mb-6">
            Simple, Transparent <span className="gradient-text">Pricing</span>
          </h2>
          <p className="text-lg text-aumraa-grey dark:text-white/60 max-w-2xl mx-auto">
            Choose the plan that fits your stage. Scale up or down as needed.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-16">
          <div className="inline-flex p-1 rounded-full bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10">
            {['monthly', 'quarterly', 'yearly'].map((period) => (
              <button
                key={period}
                onClick={() => setBillingPeriod(period)}
                className={`px-6 py-2 rounded-full text-sm font-semibold capitalize transition-all duration-300 ${billingPeriod === period
                  ? 'bg-aumraa-green text-white shadow-lg'
                  : 'text-aumraa-grey dark:text-white/60 hover:text-aumraa-black dark:hover:text-white'
                  }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {subscriptionTiers.map((tier) => (
            <div
              key={tier.id}
              className={`pricing-card flex flex-col p-8 rounded-3xl bg-white/60 dark:bg-white/5 backdrop-blur-md border transition-all duration-300 relative group overflow-hidden ${tier.highlighted
                ? 'border-aumraa-green shadow-2xl shadow-aumraa-green/10 md:-translate-y-4'
                : 'border-white/40 dark:border-white/10 hover:border-aumraa-green/30'
                }`}
            >
              {tier.highlighted && (
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-aumraa-green to-aumraa-yellow" />
              )}

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-aumraa-black dark:text-white mb-2">{tier.name}</h3>
                <p className="text-sm font-medium text-aumraa-green mb-6">{tier.subtitle}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-aumraa-black dark:text-white tracking-tight">
                    ${calculatePrice(tier.basePrice, billingPeriod).toLocaleString()}
                  </span>
                  <span className="text-aumraa-grey dark:text-white/50 text-sm">
                    {getPeriodLabel(billingPeriod)}
                  </span>
                </div>
                <p className="text-sm text-aumraa-grey dark:text-white/50 mt-4 leading-relaxed">
                  {tier.description}
                </p>
              </div>

              <div className="flex-grow">
                <div className="h-px bg-aumraa-black/5 dark:bg-white/10 mb-8" />
                <ul className="space-y-4">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-aumraa-black dark:text-white/80">
                      <CheckCircle2 size={18} className="text-aumraa-green flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 pt-8">
                <button
                  onClick={onOpenBooking}
                  className={`w-full py-4 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 ${tier.highlighted
                    ? 'bg-aumraa-green text-white hover:bg-aumraa-green-dark shadow-lg shadow-aumraa-green/20'
                    : 'bg-aumraa-black dark:bg-white/10 text-white hover:bg-aumraa-black/80 dark:hover:bg-white/20'
                    }`}
                >
                  <Calendar size={18} />
                  Get Started
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Plan Callout */}
        <div className="max-w-4xl mx-auto p-1 rounded-3xl bg-gradient-to-r from-aumraa-green/50 to-aumraa-yellow/50">
          <div className="p-8 md:p-12 rounded-[22px] bg-white dark:bg-aumraa-black relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="relative z-10 flex-1">
              <h3 className="text-2xl font-bold text-aumraa-black dark:text-white mb-2">Need a Custom Plan?</h3>
              <p className="text-aumraa-grey dark:text-white/60">
                Have specific requirements or a large-scale project? We can tailor an engagement model just for you.
              </p>
            </div>
            <div className="relative z-10">
              <button
                onClick={onOpenBooking}
                className="px-8 py-4 bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 text-aumraa-black dark:text-white font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-white/20 transition-all shadow-lg"
              >
                Contact Us
              </button>
            </div>

            {/* Decorative background */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-aumraa-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          </div>
        </div>

      </div>
    </section>
  );
}

// Contact Section
function ContactSection({ onOpenBooking }) {
  const sectionRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    assistanceType: 'Web Design',
    description: ''
  });
  const [status, setStatus] = useState(null); // 'sending', 'success', 'error'

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    // EmailJS Configuration
    const serviceID = 'service_ee3sapo';
    const templateID = 'template_wncjjcr';
    const publicKey = 'HBojnkrxT-bGGgauB';

    // Prepare template params - matching standard template conventions
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      organization: formData.organization,
      assistance_type: formData.assistanceType,
      message: formData.description,
      to_email: 'santhoshi.r@aumraa.com'
    };

    try {
      await emailjs.send(serviceID, templateID, templateParams, publicKey);
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        organization: '',
        assistanceType: 'Web Design',
        description: ''
      });
      setTimeout(() => setStatus(null), 5000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus('error');
      setTimeout(() => setStatus(null), 5000);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      const contactContents = sectionRef.current.querySelectorAll('.contact-content');
      if (contactContents.length > 0) {
        gsap.fromTo(contactContents,
          { y: 60, opacity: 0 },
          {
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
            y: 0,
            opacity: 1,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.2
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="contact"
      data-testid="contact-section"
      className="section-padding relative"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left Column: Contact Form */}
          <div className="contact-content">
            <span className="text-aumraa-green font-medium tracking-widest text-sm uppercase">Get In Touch</span>
            <h2 className="text-4xl md:text-5xl font-bold text-aumraa-black dark:text-white mt-4 mb-6">
              Let&apos;s Start a <span className="gradient-text">Conversation</span>
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {status === 'success' && (
                <div className="p-4 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                  Message sent successfully! We'll be in touch soon.
                </div>
              )}
              {status === 'error' && (
                <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-aumraa-black dark:text-white">Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    disabled={status === 'sending'}
                    value={formData.name}
                    onChange={handleChange}
                    className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-aumraa-green focus:outline-none text-aumraa-black dark:text-white"
                    placeholder="Your Name"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-aumraa-black dark:text-white">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    disabled={status === 'sending'}
                    value={formData.email}
                    onChange={handleChange}
                    className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-aumraa-green focus:outline-none text-aumraa-black dark:text-white"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-aumraa-black dark:text-white">Organization / Company (Optional)</label>
                <input
                  type="text"
                  name="organization"
                  disabled={status === 'sending'}
                  value={formData.organization}
                  onChange={handleChange}
                  className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-aumraa-green focus:outline-none text-aumraa-black dark:text-white"
                  placeholder="Company Name"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-aumraa-black dark:text-white">What can we assist you with?</label>
                <select
                  name="assistanceType"
                  disabled={status === 'sending'}
                  value={formData.assistanceType}
                  onChange={handleChange}
                  className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-aumraa-green focus:outline-none text-aumraa-black dark:text-white"
                >
                  <option>Web Design</option>
                  <option>SaaS Product</option>
                  <option>Mobile App</option>
                  <option>Design System</option>
                  <option>Audit</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-aumraa-black dark:text-white">Description / What&apos;s on your mind?</label>
                <textarea
                  name="description"
                  rows="4"
                  disabled={status === 'sending'}
                  value={formData.description}
                  onChange={handleChange}
                  className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-aumraa-green focus:outline-none text-aumraa-black dark:text-white"
                  placeholder="Project details, timeline, budget range, or just hi!"
                />
              </div>

              <div className="flex items-center gap-4 pt-4">
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="px-8 py-3 bg-aumraa-green text-white font-semibold rounded-xl hover:bg-aumraa-green-dark transition-all shadow-lg shadow-aumraa-green/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {status === 'sending' ? 'Sending...' : 'Send'}
                  {status === 'sending' && <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />}
                </button>
                <button
                  type="button"
                  onClick={onOpenBooking}
                  className="text-sm text-aumraa-green hover:underline flex items-center gap-1"
                >
                  <Calendar size={16} />
                  Schedule a meeting on Calendar
                </button>
              </div>
            </form>
          </div>

          {/* Right Column: Information & Consultation */}
          <div className="contact-content space-y-8">

            {/* Consultation Card Moved Here */}
            <div>
              <h3 className="text-xl font-bold text-aumraa-black dark:text-white mb-4">Ad-hoc Consultation</h3>
              <div className="p-8 rounded-3xl bg-gradient-to-br from-aumraa-black/90 to-aumraa-black/80 dark:from-white/10 dark:to-white/5 backdrop-blur-md border border-white/20 relative overflow-hidden shadow-2xl">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-aumraa-green/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-aumraa-yellow/10 rounded-full blur-3xl" />

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-aumraa-yellow to-aumraa-green flex items-center justify-center">
                      <Clock className="text-white" size={16} />
                    </div>
                    <span className="text-aumraa-yellow font-semibold text-xs uppercase tracking-wider">Per-Hour</span>
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-2">
                    $4,000<span className="text-base font-normal text-white/60">/hour</span>
                  </h3>

                  <p className="text-white/60 text-sm mb-6">
                    Expert consultation for strategic discussions, quick audits, or specific design challenges.
                  </p>

                  <div className="flex items-center gap-3 bg-aumraa-green/10 rounded-xl p-3 mb-6">
                    <Sparkles className="text-aumraa-green flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white text-sm font-semibold">First 3 Consultations FREE</p>
                    </div>
                  </div>

                  <button
                    onClick={onOpenBooking}
                    className="inline-flex w-full items-center justify-center gap-2 px-6 py-3 bg-white text-aumraa-black font-semibold rounded-lg hover:bg-aumraa-yellow transition-all text-sm"
                  >
                    <Calendar size={16} />
                    Book a Session
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white/60 dark:bg-white/5 backdrop-blur-md border border-white/40 dark:border-white/10 shadow-lg">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-aumraa-green/10 flex items-center justify-center">
                    <Mail className="text-aumraa-green" size={18} />
                  </div>
                  <div>
                    <div className="text-sm text-aumraa-grey dark:text-white/50">Email us at</div>
                    <div className="text-aumraa-black dark:text-white font-medium">hello@aumraa.com</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-aumraa-green/10 flex items-center justify-center">
                    <MapPin className="text-aumraa-green" size={18} />
                  </div>
                  <div>
                    <div className="text-sm text-aumraa-grey dark:text-white/50">Based in</div>
                    <div className="text-aumraa-black dark:text-white font-medium">India, Working Globally</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Pricing Notice Moved Here */}
            <div className="p-4 rounded-xl bg-aumraa-green/5 border border-aumraa-green/20">
              <p className="text-aumraa-grey dark:text-white/70 text-xs">
                <strong className="text-aumraa-black dark:text-white">All pricing is indicative.</strong> Final scope is tailored after discovery based on product maturity and needs.
              </p>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

// Footer Component
function Footer() {
  const handleNavClick = (id) => {
    scrollToSection(id, -80);
  };

  return (
    <footer data-testid="footer" className="bg-aumraa-black py-16 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-12">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-symbol.svg" alt="AUMRAA" className="h-10" />
            <span className="text-white font-bold text-xl">AUMRAA</span>
          </div>
          <div className="flex flex-wrap gap-8">
            {['Process', 'About', 'Subscription', 'Contact'].map((link) => (
              <button
                key={link}
                onClick={() => handleNavClick(link.toLowerCase())}
                className="footer-link text-white/60 hover:text-aumraa-green transition-colors"
              >
                {link}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} AUMRAA. Design-first product studio.
          </p>
          <p className="text-white/40 text-sm">
            Rooted in empowered ingenuity.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Main App Component
function App() {
  const [isDark, setIsDark] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const toggleBooking = () => setIsBookingOpen(!isBookingOpen); // Helper if needed
  const openBooking = () => setIsBookingOpen(true);

  // Initialize Lenis smooth scroll
  useEffect(() => {
    lenisInstance = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenisInstance.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Connect Lenis to GSAP ScrollTrigger
    lenisInstance.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => {
      lenisInstance.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    return () => {
      lenisInstance.destroy();
      lenisInstance = null;
    };
  }, []);

  // Theme toggle
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Track active section
  useEffect(() => {
    const sections = ['process', 'about', 'subscription', 'contact'];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    sections.forEach((section) => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className={`app-container ${isDark ? 'dark' : ''}`}>
      {/* Fixed Background for Parallax Effect */}
      <div className="fixed inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene isDark={isDark} />
        </Canvas>
      </div>

      <div className={`min-h-screen transition-colors duration-500 relative z-10 font-sans`}>
        {/* Grain overlay for texture */}
        <div className="grain-overlay opacity-30 pointer-events-none" />

        <Navigation
          isDark={isDark}
          toggleTheme={() => setIsDark(!isDark)}
          activeSection={activeSection}
        />

        <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />

        <main>
          <HeroSection isDark={isDark} onOpenBooking={openBooking} />
          <ProcessSection onOpenBooking={openBooking} />
          <ServicesSection />
          <AboutSection />
          <SubscriptionSection onOpenBooking={openBooking} />
          <ContactSection onOpenBooking={openBooking} />
        </main>

        <Footer />
      </div>
    </div>
  );
}

export default App;
